import { useState } from "react"

export interface VotingPowerData {
  epoch: number
  stakedTokens: number
  id: string
}

export function useVotingPowerData(initialData?: Omit<VotingPowerData, "id">[]) {
  const [votingPowerData, setVotingPowerData] = useState<VotingPowerData[]>(
    initialData 
      ? initialData.map(item => ({ ...item, id: Math.random().toString(36).substring(2, 11) }))
      : []
  )

  // Add a new voting power data point
  const addVotingPowerData = (epoch: number, stakedTokens: number) => {
    if (epoch <= 0 || stakedTokens <= 0) {
      return false
    }

    // Check if a data point with the same epoch already exists
    const exists = votingPowerData.some(data => data.epoch === epoch)
    if (exists) {
      return false
    }

    const newData = {
      epoch,
      stakedTokens,
      id: Math.random().toString(36).substring(2, 11),
    }

    setVotingPowerData(prev => [...prev, newData])
    return true
  }

  // Remove a voting power data point by id
  const removeVotingPowerData = (id: string) => {
    setVotingPowerData(prev => prev.filter(data => data.id !== id))
  }

  // Update a voting power data point
  const updateVotingPowerData = (id: string, epoch: number, stakedTokens: number) => {
    if (epoch <= 0 || stakedTokens <= 0) {
      return false
    }

    // Check if update would create a duplicate epoch (excluding the current item)
    const duplicateExists = votingPowerData
      .filter(data => data.id !== id)
      .some(data => data.epoch === epoch)
    
    if (duplicateExists) {
      return false
    }

    setVotingPowerData(prev => 
      prev.map(data => 
        data.id === id ? { ...data, epoch, stakedTokens } : data
      )
    )
    return true
  }

  // Clear all data points
  const clearVotingPowerData = () => {
    setVotingPowerData([])
  }

  // Calculate total voting power (sum of areas of all triangles)
  const calculateTotalVotingPower = () => {
    return votingPowerData.reduce((total, data) => {
      // Area of triangle = (1/2) * base * height = (1/2) * epoch * stakedTokens
      const votingPower = (data.epoch * data.stakedTokens) / 2
      return total + votingPower
    }, 0)
  }

  return {
    votingPowerData,
    addVotingPowerData,
    removeVotingPowerData,
    updateVotingPowerData,
    clearVotingPowerData,
    calculateTotalVotingPower
  }
} 